import { ApolloError } from "@apollo/client";
import React, { useMemo } from "react";
import { NotFound } from "src/components/Feedback/NotFound";
import { Step as StepType } from "src/components/Layout/FormStepLayout";
import { useVisibilityContext } from "src/components/Providers/FormProvider";
import {
  getFailedConstraintsValidationQuestions,
  getMissingRequiredQuestions,
  isSectionApplicableForEdit,
} from "src/services/formTemplate/section";
import * as AFF from "src/services/formTemplateFilters";
import * as AF from "src/types/formTemplate";
import * as GQL from "src/types/graphql";
import { RemoteData } from "src/types/remoteData";
import { StepDisclaimerSection } from "./StepDisclaimerSection";
import { StepGeneralSection } from "./StepGeneralSection";
import { StepPreRanking } from "./StepPreRanking";
import { StepRankSchools } from "./StepRankSchools";

type Props = {
  answers: GQL.GetFormAnswersById;
  currentStep: number;
  refetchSchoolRanks: () => Promise<unknown>;
  schoolRanksRemoteData: RemoteData<ApolloError, GQL.GetSchoolsRank>;
  previousFormSchoolIds: uuid[];
  form: GQL.FormFragment;
  sections: AF.Sections<AF.WithId>;
  formId: uuid;
  formTemplateId: string;
  applicant: AFF.Types.Applicant;
  verificationResults: GQL.FormFragment_form_verification_results[];
  headers?: React.ReactNode;
  hasBeenSubmittedBefore: boolean | null;
};
export const Step: React.FC<Props> = ({
  answers,
  refetchSchoolRanks,
  schoolRanksRemoteData,
  previousFormSchoolIds,
  form,
  sections,
  formTemplateId,
  verificationResults,
  hasBeenSubmittedBefore,
  ...props
}) => {
  const rankedSchoolIds = useMemo(() => {
    if (!schoolRanksRemoteData.hasData()) {
      return [];
    }

    return schoolRanksRemoteData.data.form_school_rank.map(
      (rankedSchool) => rankedSchool.school.id
    );
  }, [schoolRanksRemoteData]);

  const visibilityContext = useVisibilityContext();

  const applicableSections = useMemo<AF.Sections<AF.WithId>>(() => {
    if (!schoolRanksRemoteData.hasData()) {
      /*
       * Since the remote data is resolved in the parent component, this should
       * never be reached.
       */
      return sections;
    }

    return sections.filter((section) => {
      return isSectionApplicableForEdit(
        form.status,
        section,
        answers.form_answer,
        answers.grades_answer,
        answers.form_address,
        answers.custom_question_answer,
        { rankedSchoolIds, previousFormSchoolIds },
        visibilityContext
      );
    }) as AF.Sections<AF.WithId>;
  }, [
    schoolRanksRemoteData,
    sections,
    form.status,
    answers.form_answer,
    answers.grades_answer,
    answers.form_address,
    answers.custom_question_answer,
    rankedSchoolIds,
    previousFormSchoolIds,
    visibilityContext,
  ]);

  const section = applicableSections[props.currentStep - 1];
  if (!section) return <NotFound />;

  const steps: StepType[] = applicableSections.flatMap((s) => {
    if (s === undefined) {
      return [];
    }

    return [
      {
        id: s.id,
        title: s.title,
        initialFailedConstraintsValidationQuestions:
          getFailedConstraintsValidationQuestions(
            s,
            answers.form_answer,
            answers.grades_answer,
            answers.form_address,
            answers.custom_question_answer,
            answers.form_disclaimer,
            { rankedSchoolIds, previousFormSchoolIds },
            visibilityContext
          ),
        initialMissingRequiredQuestions: getMissingRequiredQuestions(
          s,
          answers.form_answer,
          answers.grades_answer,
          answers.form_address,
          answers.custom_question_answer,
          answers.form_disclaimer,
          { rankedSchoolIds, previousFormSchoolIds },
          visibilityContext
        ),
      },
    ];
  });

  switch (section.type) {
    case AF.PreRankingSectionType:
      return (
        <StepPreRanking
          key={section.id}
          answers={answers}
          refetchSchoolRanks={refetchSchoolRanks}
          schoolRanksRemoteData={schoolRanksRemoteData}
          section={section}
          allSections={applicableSections}
          steps={steps}
          verificationResults={verificationResults}
          hasBeenSubmittedBefore={hasBeenSubmittedBefore}
          previousFormSchoolIds={previousFormSchoolIds}
          formTemplateId={formTemplateId}
          enrollmentPeriodId={form.form_template.enrollment_period_id}
          {...props}
        />
      );
    case AF.SchoolRankingSectionType:
      return (
        <StepRankSchools
          key={section.id}
          formTemplateId={formTemplateId}
          enrollmentPeriodId={form.form_template.enrollment_period_id}
          section={section}
          allSections={applicableSections}
          steps={steps}
          hasBeenSubmittedBefore={hasBeenSubmittedBefore}
          refetchSchoolRanks={refetchSchoolRanks}
          schoolRanksRemoteData={schoolRanksRemoteData}
          {...props}
        />
      );
    case AF.GeneralSectionType:
      return (
        <StepGeneralSection
          key={section.id} // using `key` is important here to prevent react from reusing this component for different sections
          answers={answers}
          schoolRanksRemoteData={schoolRanksRemoteData}
          previousFormSchoolIds={previousFormSchoolIds}
          section={section}
          steps={steps}
          verificationResults={verificationResults}
          hasBeenSubmittedBefore={hasBeenSubmittedBefore}
          {...props}
        />
      );
    case AF.DisclaimerSectionType:
      return (
        <StepDisclaimerSection
          key={section.id}
          section={section}
          steps={steps}
          hasBeenSubmittedBefore={hasBeenSubmittedBefore}
          {...props}
        />
      );
  }
};
